import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';

export interface TablePriceDisplayProps {
  value: string | null;
  isEmpty?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
  /** Whether the cell is editable (affects cursor) */
  isEditable?: boolean;
  /** Whether this is a parent row (affects cursor/opacity) */
  isParent?: boolean;
  /** Click handler to start editing */
  onClick?: () => void;
  /** Whether this field was recently modified (for highlighting) */
  isRecentlyModified?: boolean;
}

/**
 * Price display component that shows the symbol in the same position as TablePriceInput
 */
export const TablePriceDisplay: React.FC<TablePriceDisplayProps> = ({
  value,
  isEmpty = false,
  hasError = false,
  errorMessage,
  className,
  tooltipContent,
  children,
  isEditable = true,
  isParent = false,
  onClick,
  isRecentlyModified = false,
}) => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const currency = store === Store.FR ? Currency.EUR : Currency.USD;

  // Extract the numeric part (remove currency symbol if present) and replace dot with comma
  const numericValue =
    value
      ?.replace(/[€$]/g, '')
      .trim()
      .replace(/\./g, ',') || null;

  const interactiveClasses = isParent
    ? 'cursor-pointer opacity-50'
    : isEditable
      ? 'cursor-pointer'
      : 'cursor-not-allowed opacity-50';

  const displayClasses = cn(
    'relative flex h-full w-full items-center rounded-none bg-transparent pr-6 text-right text-sm',
    hasError
      ? 'bg-red-50 border-l-2 border-red-500'
      : isRecentlyModified
        ? 'bg-blue-50 border-l-2 border-blue-500'
        : 'bg-transparent',
    interactiveClasses,
    className
  );

  const content = (
    <div className={displayClasses} onClick={onClick}>
      <span className="relative w-full text-right">
        {isEmpty ? (
          <span className="text-muted-foreground">
            {errorMessage ? t(errorMessage) : '—'}
          </span>
        ) : (
          numericValue || '—'
        )}
        {children}
      </span>
      {!isEmpty && numericValue && (
        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 select-none text-base text-gray-500">
          {currency}
        </span>
      )}
    </div>
  );

  if (tooltipContent || (errorMessage && !isEmpty)) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        {(errorMessage && !isEmpty) || tooltipContent ? (
          <TooltipContent>
            {errorMessage && !isEmpty ? (
              <p>{t(errorMessage)}</p>
            ) : (
              tooltipContent
            )}
          </TooltipContent>
        ) : null}
      </Tooltip>
    );
  }

  return content;
};
